import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import ReactSelect from 'react-select';
import { toast } from 'sonner';
import { Plus, X, Trash2, Edit3, Link as LinkIcon, Calendar, Image as ImageIcon } from 'lucide-react';
import { getBanners, addBanner, updateBanner, deleteBanner, getBannerAds, addBannerAd, updateBannerAd, deleteBannerAd } from '../../lib/apiService';
import { MAIN_API_BASE, BASE_URL } from '../../lib/apiConfig';
import Compressor from 'compressorjs';

const resolveImageUrl = (photoUrl, photo, folder) => {
  // photo_url is a server-relative path like /api/storage/banner_ads/banner_1.png
  if (photoUrl && typeof photoUrl === 'string' && photoUrl.startsWith('/')) {
    return `${BASE_URL}${photoUrl}`;
  }
  // fallback: construct from filename
  if (photo && typeof photo === 'string') {
    const filename = photo.split('/').pop();
    return `${MAIN_API_BASE}/storage/${folder}/${filename}`;
  }
  return '';
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    borderRadius: '0.75rem', // rounded-xl
    fontSize: '14px',
    borderColor: state.isFocused ? '#ee7923' : '#e2e8f0', // orange / slate-200
    backgroundColor: '#ffffff',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(238, 121, 35, 0.2)' : 'none',
    '&:hover': { borderColor: '#cbd5e1' }
  }),
  valueContainer: (base) => ({
    ...base,
    height: '40px',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center'
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '40px'
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '4px 8px'
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '4px 8px'
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '0.75rem',
    zIndex: 9999,
    fontSize: '14px',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)'
  }),
  menuList: (base) => ({
    ...base,
    padding: 0,
    borderRadius: '0.75rem'
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    padding: '8px 12px',
    backgroundColor: state.isSelected ? '#ee7923' : state.isFocused ? '#f1f5f9' : 'white',
    color: state.isSelected ? 'white' : '#1e293b'
  })
};

const BannersPage = () => {
  const [activeTab, setActiveTab] = useState('banners'); // 'banners' or 'ads'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form state
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [link, setLink] = useState('');
  const [sequence, setSequence] = useState(0);
  const [isActive, setIsActive] = useState('true');
  const [validTill, setValidTill] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'banners') {
        const res = await getBanners();
        if (res.status) setItems(res.data || []);
        else toast.error(res.message || 'Failed to load banners');
      } else {
        const res = await getBannerAds();
        if (res.status) setItems(res.data || []);
        else toast.error(res.message || 'Failed to load banner ads');
      }
    } catch (err) {
      toast.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openModal = (item = null) => {
    setEditingItem(item);
    if (item) {
      setLink(item.link || '');
      setSequence(item.sequence || 0);
      setIsActive(item.is_active?.toString() || 'true');
      if (activeTab === 'ads') setValidTill(item.valid_till || '');

      setPhotoPreview(resolveImageUrl(item.photo_url, item.photo, activeTab === 'banners' ? 'banners' : 'promotions'));
    } else {
      setLink('');
      setSequence(0);
      setIsActive('true');
      setValidTill('');
      setPhotoPreview('');
    }
    setPhoto(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setPhoto(null);
    setPhotoPreview('');
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      new Compressor(file, {
        quality: 0.6,
        success(result) {
          setPhoto(result);
          const reader = new FileReader();
          reader.onloadend = () => setPhotoPreview(reader.result);
          reader.readAsDataURL(result);
        },
        error(err) {
          console.error('Image compression error:', err.message);
          setPhoto(file);
          const reader = new FileReader();
          reader.onloadend = () => setPhotoPreview(reader.result);
          reader.readAsDataURL(file);
        },
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem && !photo) {
      return toast.error('Please select an image to upload.');
    }

    setIsSubmitting(true);
    const formData = new FormData();
    if (photo) formData.append('photo', photo);
    formData.append('link', link);
    formData.append('sequence', sequence);
    formData.append('is_active', isActive);

    if (activeTab === 'ads') {
      formData.append('valid_till', validTill);
    }

    try {
      let res;
      if (editingItem) {
        formData.append('id', editingItem.id);
        res = activeTab === 'banners' ? await updateBanner(formData) : await updateBannerAd(formData);
      } else {
        res = activeTab === 'banners' ? await addBanner(formData) : await addBannerAd(formData);
      }

      if (res.status) {
        toast.success(res.message);
        closeModal();
        fetchItems();
      } else {
        toast.error(res.message || 'Operation failed');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const res = activeTab === 'banners' ? await deleteBanner(id) : await deleteBannerAd(id);
      if (res.status) {
        toast.success('Deleted successfully');
        fetchItems();
      } else {
        toast.error(res.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error('Error deleting item');
    }
  };

  const renderTabBtn = (id, label) => {
    const isActive = activeTab === id;
    return (
      <button
        onClick={() => setActiveTab(id)}
        className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 ${isActive
            ? 'border-[#ee7923] text-[#ee7923] bg-orange-50/50'
            : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
          }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <Helmet>
        <title>Banners | JoldiPabo Admin</title>
      </Helmet>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Banners & Banner Ads
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage app carousel banners and banner ads.</p>
        </div>
        <Button onClick={() => openModal()} className="gap-2 bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4" /> Add New
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6 shadow-sm">
        <div className="flex border-b border-slate-200">
          {renderTabBtn('banners', 'App Banners')}
          {renderTabBtn('ads', 'Banner Ads')}
        </div>

        <div className="p-6 bg-slate-50/50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
              <ImageIcon className="w-12 h-12 mb-3 text-slate-300" />
              <p>No {activeTab === 'banners' ? 'banners' : 'banner ads'} found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.map(item => {
                const imgUrl = resolveImageUrl(item.photo_url, item.photo, activeTab === 'banners' ? 'banners' : 'promotions');
                const active = item.is_active?.toString() === 'true' || item.is_active === '1';

                return (
                  <Card key={item.id} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow group flex flex-col">
                    <div className="relative aspect-[16/9] bg-slate-100 overflow-hidden flex items-center justify-center">
                      <img
                        src={imgUrl}
                        alt="Banner"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        {active ? (
                          <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-wide">
                            ACTIVE
                          </span>
                        ) : (
                          <span className="bg-slate-100 text-slate-600 text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-sm uppercase tracking-wide">
                            INACTIVE
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col justify-between">
                      <div className="space-y-1.5">
                        {item.link ? (
                          <div className="flex items-start gap-2 text-sm">
                            <LinkIcon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                            <a href={item.link} target="_blank" rel="noreferrer" className="text-[#ee7923] hover:underline break-all line-clamp-2">
                              {item.link}
                            </a>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm text-slate-400 italic">
                            <LinkIcon className="w-4 h-4 shrink-0" /> No link provided
                          </div>
                        )}

                        {activeTab === 'ads' && item.valid_till && (
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                            Valid till: <span className="font-medium text-slate-900">{new Date(item.valid_till).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          </div>
                        )}
                        <div className="text-xs text-slate-500">
                          Sequence: <span className="font-medium text-slate-900">{item.sequence}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-3">
                        <Button variant="outline" size="sm" className="flex-1 h-8 text-xs gap-1.5 text-slate-600" onClick={() => openModal(item)}>
                          <Edit3 className="w-3.5 h-3.5" /> Edit
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 h-8 text-xs gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {editingItem ? 'Edit' : 'Add New'} {activeTab === 'banners' ? 'Banner' : 'Banner Ad'}
              </h2>
              <button onClick={closeModal} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Image Photo *</label>
                <div className="border-2 border-dashed border-slate-200 rounded-xl p-1 text-center hover:bg-slate-50 transition-colors relative">
                  {photoPreview ? (
                    <div className="relative">
                      <img src={photoPreview} alt="Preview" className="w-full h-auto max-h-48 object-cover rounded-lg border border-slate-200" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 hover:opacity-100 transition-opacity rounded-lg cursor-pointer">
                        <span className="text-white text-sm font-medium">Change Photo</span>
                        <input type="file" accept="image/*" onChange={handlePhotoChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                      </div>
                    </div>
                  ) : (
                    <div className="py-6">
                      <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500 mb-1">Click to upload or drag and drop</p>
                      <p className="text-xs text-slate-400">JPG, PNG, WEBP (16:9 ratio recommended)</p>
                      <input type="file" accept="image/*" onChange={handlePhotoChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Target Link</label>
                <Input
                  value={link}
                  onChange={e => setLink(e.target.value)}
                  placeholder="https://example.com"
                  className="rounded-xl"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Sequence Order</label>
                  <Input
                    type="number"
                    value={sequence}
                    onChange={e => setSequence(e.target.value)}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
                  <ReactSelect
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPosition="fixed"
                    options={[
                      { value: 'true', label: 'Active' },
                      { value: 'false', label: 'Inactive' }
                    ]}
                    value={
                      isActive === 'true'
                        ? { value: 'true', label: 'Active' }
                        : { value: 'false', label: 'Inactive' }
                    }
                    onChange={opt => setIsActive(opt?.value || 'true')}
                    isSearchable={false}
                  />
                </div>
              </div>

              {activeTab === 'ads' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Valid Till Date</label>
                  <Input
                    type="date"
                    value={validTill}
                    onChange={e => setValidTill(e.target.value)}
                    required
                    className="rounded-xl"
                  />
                </div>
              )}

              <div className="pt-2">
                <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-10" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannersPage;
